/**
 * ChangePatientButton.tsx
 *
 * The control that opens the inline patient search on the appointment modal, so a
 * booking made under the wrong name can be reassigned instead of deleted and re-entered.
 *
 * It used to be a bare `RefreshCw` glyph in a 24px ghost button with no label and no
 * accessible name — a support ticket reported the swap as an entirely missing feature
 * because nobody could find it, and the icon read as "reload". Hence the visible word,
 * the border, and the person-with-magnifier icon: the label is the point of this
 * component, not decoration on it.
 *
 * `size="xs"` is deliberate. It maps to `h-6`, which is the height the old icon button
 * occupied, so the modal header row keeps its density while gaining a real affordance.
 * Note that `Button` skips its 44px touch floor at `xs`, so the mobile hit area matches
 * the old control rather than improving on it.
 *
 * Rendered twice — the edit-mode and create-mode headers of UnifiedAppointmentModal —
 * which is why it lives here rather than inline in both.
 */
import { UserSearch } from 'lucide-react';
import { Button } from '@/components/ui/Button';

export interface ChangePatientButtonProps {
  /** Toggles the inline patient search open and closed. */
  onClick: () => void;
  /** Search panel is currently open. Consumed only for `aria-pressed`. */
  active: boolean;
}

export function ChangePatientButton({ onClick, active }: ChangePatientButtonProps) {
  return (
    /* `Button` already defaults `type="button"`, which matters here: the edit-mode
       header sits inside `#unified-appt-form` and a submit default would post it. */
    <Button
      variant="secondary"
      size="xs"
      aria-pressed={active}
      aria-label="Change the patient on this appointment"
      title="Change the patient on this appointment"
      leftIcon={<UserSearch className="h-3.5 w-3.5" />}
      onClick={onClick}
    >
      Change
    </Button>
  );
}
